// Environment config (specs/11_client_environments.md §2/§3.3 — Vite VITE_* build-time env).
// Dev defaults target the v3-dev edge directly; staging/prod come from .env.* using the
// api.{region}.{env}.ls-cloud.net template. X-Client-Key is a feature gate, not a secret
// (00 §1), so it may live in web config.
//
// Region model (spec 11 §2/§59): the env pins a REST *host template* + a build-time
// REGION_HOSTS list — NOT a single baked host. The region is AUTO-DETECTED pre-auth
// (IP via /v3/geo, OS/locale fallback — see lib/regionDetect.ts), never user-selected,
// and the API base is built from the template. The detected region is persisted
// (localStorage) so it survives reloads and drives the region field on register/resend/
// forgot bodies. Post-login the account's region is authoritative (JWT `region` claim).

export interface AppConfig {
  envName: string;
  /** Resolved REST base for the currently-selected region (getter). */
  apiBase: string;
  signalingUrl: string;
  clientId: string;
  clientKey: string;
  clientVersion: string;
  /** Currently-selected region (getter, persisted). */
  region: string;
  /** Build-time app version (A6 關於/版本, spec 20 §8.2). */
  appVersion: string;
}

const env = import.meta.env;

// __APP_VERSION__ is injected by Vite `define`; under vitest (no define) it is undefined.
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0-dev';
const clientVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

// REST host template (spec 11 §3.3): may contain the literal `{region}` placeholder
// (staging/prod), or be a single literal host with no placeholder (local/v3-dev). Falls
// back to the legacy single-host VITE_API_BASE for back-compat, then the v3-dev edge.
// ls-loop（MW）：VITE_SIGNALING_URL 給相對路徑（例 "/ws"）＝跟網頁同網域（本機反向代理 / 正式同網域部署），
// 依網頁的協定組成 wss:// 或 ws://。不用在建置時寫死主機（本機雲端的區網 IP 會變）。
function sameOriginWs(u: string): string {
  if (!u.startsWith('/') || typeof location === 'undefined') return u;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${u}`;
}

const apiHostTmpl = (env.VITE_API_HOST_TMPL ?? env.VITE_API_BASE ?? 'https://v3-api-dev.ls-dg.net').trim();

// Build-time region list (spec 11 §3.3 VITE_REGION_HOSTS). Defaults to all four so the
// detection considers all four; a region-locked build narrows the legal set.
export const regionHosts: string[] = (env.VITE_REGION_HOSTS ?? 'us,eu,apac,cn')
  .split(',')
  .map((r) => r.trim())
  .filter(Boolean);

const REGION_KEY = 'ls_region';

function isValidRegion(r: string | null | undefined): r is string {
  return !!r && regionHosts.includes(r);
}

// Default region: legacy VITE_REGION if listed, else apac if listed, else the first entry.
const defaultRegion = isValidRegion(env.VITE_REGION)
  ? env.VITE_REGION
  : regionHosts.includes('apac')
    ? 'apac'
    : (regionHosts[0] ?? 'apac');

function readStoredRegion(): string {
  try {
    const s = localStorage.getItem(REGION_KEY);
    if (isValidRegion(s)) return s;
  } catch {
    // localStorage unavailable (SSR / privacy mode) — fall through to default.
  }
  return defaultRegion;
}

/** Currently-selected region (validated against the build-time list). */
export function getRegion(): string {
  return readStoredRegion();
}

/** Persist the user's region choice (no-op if not in the build-time list). */
export function setRegion(region: string): void {
  if (!isValidRegion(region)) return;
  try {
    localStorage.setItem(REGION_KEY, region);
  } catch {
    // best-effort; a non-persisted choice still applies for this tab via the getter path.
  }
}

/** Pure template substitution (spec 11 §3.3): replaces `{region}` in a host template, or
 *  returns the literal host when there is no placeholder (dev single-host). Trailing slash
 *  trimmed. Exported for testability. */
export function applyRegionTemplate(tmpl: string, region: string): string {
  const base = tmpl.includes('{region}') ? tmpl.replace(/\{region\}/g, region) : tmpl;
  return base.replace(/\/+$/, '');
}

/** REST base for a given region, using the build-time host template. */
export function apiBaseFor(region: string): string {
  return applyRegionTemplate(apiHostTmpl, region);
}

export const config: AppConfig = {
  envName: env.VITE_ENV_NAME ?? 'dev',
  get apiBase() {
    return apiBaseFor(getRegion());
  },
  signalingUrl: sameOriginWs(env.VITE_SIGNALING_URL ?? 'wss://v3-signalca-dev.ls-dg.net/ws'),
  clientId: env.VITE_CLIENT_ID ?? 'c-ls-home-web',
  // APP#64: the dev/GA client key is deployment input.  A source fallback
  // would make a rotated key reusable from Git history and silently undo the
  // rotation on the next plain build.  Keeping the empty value fail-closed
  // lets the API return INVALID_CLIENT until the deployment supplies it.
  clientKey: env.VITE_CLIENT_KEY?.trim() ?? '',
  // Build-time version → X-Client-Version(APP#53;缺 header 撞 force 樓地板會 426)
  clientVersion,
  get region() {
    return getRegion();
  },
  appVersion,
};
